// cesium.ts

import { Cartesian3, createOsmBuildingsAsync, Ion, Math as CesiumMath, Terrain, Viewer, IonResource, Cesium3DTileset, JulianDate, ClockRange, CameraEventType, Color, Cesium3DTileStyle, ScreenSpaceEventType, Cesium3DTileFeature } from 'cesium';
import "cesium/Build/Cesium/Widgets/widgets.css";



// Your access token can be found at: https://ion.cesium.com/tokens.
// This is the default access token from your ion account
Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI1MjI3NTNhZC0zNTljLTRlNWItOTc0ZS0yYjNjODhlZWU1OGYiLCJpZCI6MTk0OTI5LCJpYXQiOjE3MDc1MTE5NjR9.bW1to-Dax47Ra0BiGefax1eFdsOkPvWSaXSjCodQHgE';

interface StyleCondition {
  property: string;
  value: string;
  color: string;
  opacity: number;
}

interface InitializeCesiumOptions {
  onObjectClick?: (attributes: { [key: string]: any }) => void;
}

function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

export async function initializeCesium(options: InitializeCesiumOptions = {}) {
  try {
    const isMobileDevice = isMobile();

    const viewerOptions: any = {
      terrain: Terrain.fromWorldTerrain(),
      timeline: false,
      animation: false,
      sceneModePicker: false,
      baseLayerPicker: false,
      infoBox: false,
    };

    if (isMobileDevice) {
      console.log('Mobile device detected. Applying performance optimizations.');
      viewerOptions.requestRenderMode = true;
      viewerOptions.maximumRenderTimeChange = Infinity;
      
      // Additional mobile performance optimizations
      viewerOptions.scene3DOnly = true; // Disable 2D and Columbus view
      viewerOptions.shadows = false; // Disable shadows on mobile
      viewerOptions.terrainShadows = false;
    }

    // Initialize the Cesium Viewer in the HTML element with the `cesiumContainer` ID.
    const viewer = new Viewer('cesiumContainer', viewerOptions);

    if (isMobileDevice) {
      viewer.scene.globe.maximumScreenSpaceError = 4.0; // Lower detail
      
      // Dynamically adjust resolution scale based on device pixel ratio
      const pixelRatio = window.devicePixelRatio || 1;
      if (pixelRatio >= 3) {
        viewer.resolutionScale = 0.9; // High-end mobile
      } else if (pixelRatio >= 2) {
        viewer.resolutionScale = 0.8; // Mid-range mobile
      } else {
        viewer.resolutionScale = 0.7; // Low-end mobile
      }

      viewer.scene.postProcessStages.fxaa.enabled = false;
      
      // Additional mobile optimizations
      viewer.scene.fog.enabled = false;
      viewer.scene.skyAtmosphere.show = false;
      
      // Disable some expensive features
      viewer.scene.globe.enableLighting = false;
      viewer.scene.globe.depthTestAgainstTerrain = false;
    }

    const scene = viewer.scene;
    // Only show sky atmosphere on desktop
    scene.skyAtmosphere.show = !isMobileDevice;

    // Add event listeners for settings changes
    window.addEventListener('depthTestChange', ((event: CustomEvent) => {
      scene.globe.depthTestAgainstTerrain = event.detail.enabled;
    }) as EventListener);

    window.addEventListener('shadowsChange', ((event: CustomEvent) => {
      scene.globe.enableLighting = event.detail.enabled;
    }) as EventListener);

    // Add Cesium OSM Buildings, a global 3D buildings layer.
    try {
      const buildingTileset = await createOsmBuildingsAsync();
      viewer.scene.primitives.add(buildingTileset);
    } catch (error) {
      console.warn('Failed to load OSM buildings on mobile:', error);
    }

    // Load and add the specified assets
    const assetIds = [ 3442167];
    let tilesets: any[] = [];
    try {
      tilesets = await Promise.all(
        assetIds.map(async (id) => {
          try {
            const tileset = await Cesium3DTileset.fromUrl(
              await IonResource.fromAssetId(id)
            );
            viewer.scene.primitives.add(tileset);
            
            // Only enable these features on desktop to reduce mobile performance impact
            if (!isMobileDevice) {
              viewer.scene.globe.depthTestAgainstTerrain = true;
              viewer.scene.globe.enableLighting = true;
              viewer.scene.globe.baseColor = Color.TRANSPARENT;
            }
            
            // Initial style conditions - only apply on desktop to avoid mobile issues
              const initialConditions = [
                {
                  property: '_6238BKlassifizierungBauteilgruppe',
                  value: 'Lichtraum',
                  color: 'red',
                  opacity: 0
                },
                {
                  property: '_6238BKlassifizierungBauteil',
                  value: 'Bohrpfahl',
                  color: 'red',
                  opacity: 0
                },
                {
                  property: '_6238BKlassifizierungBauteilgruppe',
                  value: 'Außenschale',
                  color: 'black',
                  opacity: 0.8
                }
              ];

              const updateTilesetStyle = (conditions: StyleCondition[]) => {
                try {
                  // Try to apply the complex style first
                  const styleConditions = conditions.map(condition => [
                    `\${${condition.property}} === '${condition.value}'`,
                    `color('${condition.color}', ${condition.opacity})`
                  ]);
                  
                  // Add default condition
                  styleConditions.push(['true', "color('white', 1.0)"]);

                  tileset.style = new Cesium3DTileStyle({
                    color: {
                      conditions: styleConditions
                    }
                  });
                } catch (styleError) {
                  console.warn('Complex style failed, using fallback:', styleError);
                  // If complex style fails, use simple fallback
                  try {
                    tileset.style = new Cesium3DTileStyle({
                      color: "color('white', 1.0)"
                    });
                  } catch (fallbackError) {
                    console.warn('Fallback style also failed:', fallbackError);
                  }
                }
              };

              // Set initial style with delay to ensure tileset is fully loaded
              // setTimeout(() => {
              //   updateTilesetStyle(initialConditions);
              // }, 1000);

              // Add event listener for style changes
              window.addEventListener('styleConditionsChange', ((event: CustomEvent) => {
                updateTilesetStyle(event.detail.conditions);
              }) as EventListener);
            

            return tileset;
          } catch (tilesetError) {
            console.error(`Failed to load tileset ${id}:`, tilesetError);
            return null;
          }
        })
      );
      
      // Filter out any failed tilesets
      tilesets = tilesets.filter(tileset => tileset !== null);
    } catch (error) {
      console.error('Error loading assets:', error);
    }

    // Zoom to the first tileset's bounding sphere
    if (tilesets.length > 0) {
      viewer.zoomTo(tilesets[0]);
    }

    // Set up camera constraints
    viewer.scene.screenSpaceCameraController.minimumZoomDistance = 1.0;
    
    // Configure camera controls
    const controller = viewer.scene.screenSpaceCameraController;
    if (isMobileDevice) {
      // Use Cesium's default touch behavior which works better on mobile
      // Just ensure all controls are enabled
      controller.enableRotate = true;
      controller.enableTranslate = true;
      controller.enableZoom = true;
      controller.enableTilt = true;
      
      // Use default touch event types (let Cesium handle it)
      // This provides better touch gesture recognition
    } else {
      // Desktop controls
      controller.tiltEventTypes = [CameraEventType.RIGHT_DRAG];
      controller.rotateEventTypes = [CameraEventType.LEFT_DRAG];
      controller.zoomEventTypes = [CameraEventType.WHEEL, CameraEventType.PINCH];
      controller.translateEventTypes = [CameraEventType.MIDDLE_DRAG];

      // Enable all navigation controls
      controller.enableRotate = true;
      controller.enableTranslate = true;
      controller.enableZoom = true;
      controller.enableTilt = true;
    }
    
    // Disable inertia for more precise control
    controller.inertiaSpin = 0.0;
    controller.inertiaTranslate = 0.0;
    controller.inertiaZoom = 0.0;

    // Add click handler for picking objects if a callback is provided
    if (options.onObjectClick) {
      viewer.screenSpaceEventHandler.setInputAction((click: any) => {
        try {
          const pickedObject = viewer.scene.pick(click.position);

          if (pickedObject) {
            const feature = pickedObject as any;
            const content = feature._content;
            
            if (content && content.batchTable && content.batchTable._propertyTable) {
              const propertyTable = content.batchTable._propertyTable;
              const metadataTable = propertyTable._metadataTable;

              if (metadataTable && typeof metadataTable.getProperty === 'function') {
                try {
                  const propertyIds = metadataTable.getPropertyIds();
                  const attributes: { [key: string]: any } = {};
                  const batchId = feature._batchId;

                  if (propertyIds && Array.isArray(propertyIds)) {
                    propertyIds.forEach((id: string) => {
                      try {
                        attributes[id] = metadataTable.getProperty(batchId, id);
                      } catch (propError) {
                        console.warn(`Failed to get property ${id}:`, propError);
                      }
                    });

                    console.log('SUCCESS: Attributes from metadata table:', attributes);
                    options.onObjectClick?.(attributes);
                  }
                } catch (metadataError) {
                  console.warn('Error accessing metadata properties:', metadataError);
                }
              }
            }
          }
        } catch (clickError) {
          console.warn('Error in click handler:', clickError);
        }
      }, ScreenSpaceEventType.LEFT_CLICK);
    }
  } catch (error) {
    console.error('Error initializing Cesium:', error);
  }
}

document.addEventListener("DOMContentLoaded", function () {
  const depthTestCheckbox = document.getElementById("toggleDepthTest") as HTMLInputElement;
  const shadowsCheckbox = document.getElementById("toggleShadows") as HTMLInputElement;

  function updateSettings() {
    const viewer = (window as any).cesiumViewer;
    if (viewer) {
      viewer.scene.globe.depthTestAgainstTerrain = depthTestCheckbox.checked;
      viewer.shadows = shadowsCheckbox.checked;
    }
  }

  if (depthTestCheckbox && shadowsCheckbox) {
    depthTestCheckbox.addEventListener("change", updateSettings);
    shadowsCheckbox.addEventListener("change", updateSettings);
  }
}); 